/**
 * GRI Progress Sidebar Component
 *
 * Displays progress breakdown by standard and provides quick navigation.
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
} from '@mui/material';
import {
  CheckCircle,
  RadioButtonUnchecked,
  Schedule,
  Send,
  Verified,
  Cancel,
} from '@mui/icons-material';
import { SessionProgress, GRISession, SessionStatus } from '../../services/griService';

interface GRIProgressSidebarProps {
  progress: SessionProgress;
  session: GRISession | null;
  onStandardClick: (standardCode: string) => void;
}

const STATUS_CONFIG: Record<
  SessionStatus,
  { color: string; bgColor: string; icon: React.ReactElement; label: string }
> = {
  draft: {
    color: '#6b7280',
    bgColor: '#f3f4f6',
    icon: <Schedule fontSize="small" />,
    label: 'Draft',
  },
  submitted: {
    color: '#2563eb',
    bgColor: '#dbeafe',
    icon: <Send fontSize="small" />,
    label: 'Submitted',
  },
  approved: {
    color: '#059669',
    bgColor: '#d1fae5',
    icon: <Verified fontSize="small" />,
    label: 'Approved',
  },
  rejected: {
    color: '#dc2626',
    bgColor: '#fee2e2',
    icon: <Cancel fontSize="small" />,
    label: 'Rejected',
  },
};

export const GRIProgressSidebar: React.FC<GRIProgressSidebarProps> = ({
  progress,
  session,
  onStandardClick,
}) => {
  const statusConfig = session ? STATUS_CONFIG[session.status] : STATUS_CONFIG.draft;

  return (
    <Card
      sx={{
        border: '1px solid #e5e7eb',
        boxShadow: 'none',
        position: 'sticky',
        top: 16,
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Status Badge */}
        <Box sx={{ mb: 2 }}>
          <Chip
            icon={statusConfig.icon}
            label={statusConfig.label}
            size="small"
            sx={{
              backgroundColor: statusConfig.bgColor,
              color: statusConfig.color,
              fontWeight: 600,
              '& .MuiChip-icon': { color: statusConfig.color },
            }}
          />
        </Box>

        {/* Overall Progress */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#111827', mb: 1 }}>
            Overall Progress
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <LinearProgress
              variant="determinate"
              value={progress.completion_percentage}
              sx={{
                flexGrow: 1,
                height: 8,
                borderRadius: 4,
                backgroundColor: '#e5e7eb',
                '& .MuiLinearProgress-bar': {
                  backgroundColor:
                    progress.completion_percentage === 100 ? '#059669' : '#3b82f6',
                },
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 40 }}>
              {progress.completion_percentage.toFixed(0)}%
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: '#6b7280' }}>
            {progress.answered_questions} of {progress.total_questions} questions answered
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Progress by Standard */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#111827', mb: 1 }}>
          Progress by Standard
        </Typography>

        <List dense disablePadding>
          {progress.standards_progress.map((standard) => {
            const isComplete = standard.percentage === 100;
            const hasProgress = standard.answered > 0;

            return (
              <ListItemButton
                key={standard.standard_code}
                onClick={() => onStandardClick(standard.standard_code)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  py: 1,
                  '&:hover': { backgroundColor: '#f3f4f6' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {isComplete ? (
                    <CheckCircle sx={{ color: '#059669', fontSize: 18 }} />
                  ) : (
                    <RadioButtonUnchecked
                      sx={{ color: hasProgress ? '#3b82f6' : '#d1d5db', fontSize: 18 }}
                    />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: '#374151',
                          fontSize: '0.8125rem',
                        }}
                      >
                        {standard.standard_code}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: isComplete ? '#059669' : '#6b7280',
                          fontWeight: isComplete ? 600 : 400,
                        }}
                      >
                        {standard.answered}/{standard.total}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <LinearProgress
                      variant="determinate"
                      value={standard.percentage}
                      sx={{
                        mt: 0.5,
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: '#e5e7eb',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: isComplete ? '#059669' : '#3b82f6',
                        },
                      }}
                    />
                  }
                />
              </ListItemButton>
            );
          })}
        </List>

        {/* Session Info */}
        {session && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>
                Reporting Period
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>
                {session.reporting_period}
              </Typography>
            </Box>

            {session.submitted_at && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>
                  Submitted
                </Typography>
                <Typography variant="body2" sx={{ color: '#374151' }}>
                  {new Date(session.submitted_at).toLocaleDateString()}
                </Typography>
              </Box>
            )}

            {session.approved_at && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>
                  Approved
                </Typography>
                <Typography variant="body2" sx={{ color: '#374151' }}>
                  {new Date(session.approved_at).toLocaleDateString()} by {session.approved_by}
                </Typography>
              </Box>
            )}

            {session.rejection_reason && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  backgroundColor: '#fef2f2',
                  borderRadius: 1,
                  border: '1px solid #fecaca',
                }}
              >
                <Typography variant="caption" sx={{ color: '#dc2626', display: 'block', mb: 0.5 }}>
                  Rejection Reason
                </Typography>
                <Typography variant="body2" sx={{ color: '#7f1d1d' }}>
                  {session.rejection_reason}
                </Typography>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default GRIProgressSidebar;
